// src/pages/Home.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Music,
  Phone,
  LogOut,
  Calendar,
  DollarSign,
  Star as StarIcon,
  LifeBuoy,
  Gift,
  CreditCard,
  Wallet,
  Coins,
} from "lucide-react";
import { Sparkles } from "lucide-react";
import EditProfilePopup from "../../components/EditProfilePopup";
import { API_ENDPOINTS } from "../../lib/config";
import { fetchWithAuth } from "../../lib/authService";

export default function Home() {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({});
  const [ratings, setRatings] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [activeTab, setActiveTab] = useState("events");
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileReloadKey, setProfileReloadKey] = useState(0);

  // Support form state
  const [supportReason, setSupportReason] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [supportResponse, setSupportResponse] = useState("");
  const [income, setIncome] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [performerEvents, setPerformerEvents] = useState([]);
  const [coinsGifted, setCoinsGifted] = useState([]);
  const [totalCoins, setTotalCoins] = useState(0);
  const [totalCoinValue, setTotalCoinValue] = useState(0); // value of received coins in ₹
  const [totalPaidCoins, setTotalPaidCoins] = useState(0); // total coins paid
  const [paidCoinValue, setPaidCoinValue] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem("tringAuthToken");
        if (!token) {
          navigate("/");
          return;
        }

        // 1️⃣ Partner profile
        const profRes = await fetchWithAuth(API_ENDPOINTS.PARTNER);
        if (!profRes.ok) throw new Error("Unauthorized");
        const partner = await profRes.json();
        setProfileData(partner);

        // 2️⃣ Performer events
        const perfEvRes = await fetchWithAuth(
          API_ENDPOINTS.EVENT_PERFORMER_BUSINESS
        );
        const perfEvents = await perfEvRes.json();

        // Get unique venueIds
        const uniqueVenueIds = [...new Set(perfEvents.map((ev) => ev.venueId))];

        // Fetch all venues in parallel, only once per venueId
        const venueMap = {};
        await Promise.all(
          uniqueVenueIds.map(async (venueId) => {
            try {
              const venueRes = await fetchWithAuth(
                API_ENDPOINTS.VENUES(venueId)
              );
              const venue = await venueRes.json();
              venueMap[venueId] = venue;
            } catch {
              venueMap[venueId] = {};
            }
          })
        );

        // Attach venue info to each event
        const eventsWithVenue = perfEvents.map((ev) => ({
          ...ev,
          photoUrl: ev.photoUrl,
          address: venueMap[ev.venueId]?.address || {},
        }));
        setPerformerEvents(eventsWithVenue);

        // 3️⃣ Ratings for this partner
        const rRes = await fetchWithAuth(API_ENDPOINTS.RATINGS(partner.id));
        if (!rRes.ok) throw new Error(`Ratings fetch failed: ${rRes.status}`);
        const rData = await rRes.json();
        const comments = rData.comments || [];
        setRatings(comments);

        // Compute average (rounded)
        if (comments.length) {
          const sum = comments.reduce((acc, c) => acc + (c.rating || 0), 0);
          setAvgRating(Math.round(sum / comments.length));
        }

        // 4️⃣ Income
        const incomeRes = await fetchWithAuth(
          API_ENDPOINTS.TRANSACTIONS_EARNINGS
        );
        const incomeData = await incomeRes.json();
        setIncome(incomeData);

        // 5️⃣ Coins gifted
        const coinsRes = await fetchWithAuth(API_ENDPOINTS.PARTNER_GIFTS);
        const coinsData = await coinsRes.json();
        setCoinsGifted(coinsData.earnings || []);
        setTotalCoins(coinsData.totalEarnings || 0);
        setTotalCoinValue(coinsData.totalCoinValue || 0);
        setTotalPaidCoins(coinsData.totalPaidEarnings || 0);
        setPaidCoinValue(coinsData.totalPaidCoinValue || 0);
      } catch (err) {
        console.error("Data loading error:", err);
      }
    };
    loadData();
  }, [showEditProfile, profileReloadKey]);

  useEffect(() => {
    const total = income.reduce((acc, i) => acc + i.totalEarnings, 0);
    setTotalEarnings(total);
  }, [income]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatTime = (iso) =>
    new Date(iso).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const formatDateTime = (iso) => {
    const date = new Date(iso);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const renderStars = (count) => (
    <div className="flex gap-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <StarIcon
          key={i}
          size={20}
          className={`
          ${
            i < count
              ? "text-yellow-400 stroke-yellow-400 fill-logo"
              : "text-gray-300 stroke-gray-300 fill-logo"
          }
          stroke-2
        `}
        />
      ))}
    </div>
  );

  // Support form submit handler
  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSupportResponse("");
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.USERS_SUPPORT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reason: supportReason,
          message: supportMessage,
        }),
      });
      console.log("📡 support response:", res);
      const text = await res.text();
      setSupportResponse(text);
      if (res.ok) {
        setSupportReason("");
        setSupportMessage("");
      }
    } catch (err) {
      setSupportResponse("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-purple-50 to-white w-[1200px]">
      {/* Profile Header */}
      <section className="py-8">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center md:items-center gap-6">
          <motion.div
            className="rounded-full border-4 border-white overflow-hidden h-32 w-32 md:h-40 md:w-40 bg-purple-200"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img
              src={profileData.avatar}
              alt="DJ avatar"
              className="object-cover h-full w-full"
              loading="lazy"
            />
          </motion.div>
          <div className="flex-1 flex flex-col md:items-start items-center">
            <motion.h1
              className="text-3xl font-bold text-logo"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
            >
              {profileData.display_name}
            </motion.h1>
            <motion.div
              className="flex items-center justify-start gap-2 text-logo mt-2"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Phone size={16} />
              <span>
                {profileData?.phone_number
                  ? profileData.phone_number.startsWith("+91")
                    ? profileData.phone_number
                    : `+91${profileData.phone_number}`
                  : ""}
              </span>
            </motion.div>
          </div>

          <div className="flex flex-row md:flex-col gap-4">
            <motion.button
              onClick={() => setShowEditProfile(true)}
              className="flex items-center gap-2 bg-logo text-white px-4 py-2 rounded hover:bg-logo transition-colors text-sm md:text-base active:scale-95"
              type="button"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Edit Profile
            </motion.button>
            <motion.button
              onClick={() => {
                localStorage.removeItem("tringAuthToken");
                localStorage.removeItem("isBusiness");
                navigate("/");
              }}
              type="button "
              className="flex items-center gap-2 bg-white text-red-600 border-2 border-red-600 hover:bg-red-50 px-4 py-2 rounded text-sm md:text-base"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <LogOut size={16} />
              <span>Logout</span>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="container mx-auto px-2">
        <div className="grid grid-cols-4 mb-8 bg-white overflow-hidden">
          {[
            { id: "events", label: "Events", Icon: Calendar },
            { id: "coins", label: "Gifts", Icon: Gift },
            { id: "ratings", label: "Ratings", Icon: StarIcon },
            { id: "support", label: "Support", Icon: LifeBuoy },
          ].map(({ id, label, Icon }) => (
            <button
              type="button"
              key={id}
              whileTap={{ scale: 0.97 }}
              onClick={() => setActiveTab(id)}
              className={`flex items-center justify-center rounded-lg px-1 gap-1 py-2 text-sm font-semibold transition-all border border-gray-100 ${
                activeTab === id
                  ? "bg-logo text-white"
                  : "text-logo hover:bg-purple-50"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content Container with fixed min height and full width */}
        <div className="w-full min-h-[400px] flex flex-col">
          {/* EVENT OVERVIEW */}
          {activeTab === "events" && (
            <div className="space-y-6 pb-16">
              {performerEvents.length === 0 ? (
                <div className="text-center text-gray-500">
                  No events found.
                </div>
              ) : (
                performerEvents
                  .slice()
                  .sort((a, b) => new Date(b.startAt) - new Date(a.startAt)) // latest first
                  .map((ev, i) => (
                    <motion.div
                      key={ev.id}
                      className="bg-white rounded-2xl shadow-md overflow-hidden"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <img
                        src={
                          ev.photoUrl ||
                          ev.venuePhotoUrl ||
                          "https://via.placeholder.com/400x200"
                        }
                        alt={ev.name}
                        className="w-full h-40 object-cover"
                      />
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                          {ev.name}
                        </h3>
                        <p className="text-sm text-black-600 mb-2">
                          {ev.address?.formatted_address || ""}
                        </p>
                        <p className="text-xm text-black -500 mb-2">
                          {formatDate(ev.startAt)} {formatTime(ev.startAt)}
                        </p>
                      </div>
                    </motion.div>
                  ))
              )}
            </div>
          )}

          {/* COINS GIFTED */}
          {activeTab === "coins" && (
            <div className="space-y-6 px-4 py-6">
              {/* Total Coins Card */}

              {/* Header Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                {/* Total Coins Received */}
                <motion.div
                  className="bg-white rounded-xl p-6 shadow flex items-center gap-4 border-2 border-purple-500"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                    <Coins className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">
                      Total Coins Received
                    </span>
                    <span className="text-3xl font-bold text-gray-900">
                      {totalCoins.toLocaleString()} Coins
                    </span>
                    <span className="text-sm text-gray-500">
                      ₹ {totalCoinValue.toFixed(2)}
                    </span>
                  </div>
                </motion.div>

                {/* Total Coins Paid */}
                <motion.div
                  className="bg-white rounded-xl p-6 shadow-red-800 flex items-center gap-4 border-2 border-green-500"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="p-3 rounded-full bg-green-100 text-green-600">
                    <Wallet className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">
                      Total Coins Paid
                    </span>
                    <span className="text-3xl font-bold text-gray-900">
                      {totalPaidCoins.toLocaleString()} Coins
                    </span>
                    <span className="text-sm text-gray-500">
                      ₹ {paidCoinValue.toFixed(2)}
                    </span>
                  </div>
                </motion.div>

                {/* To Be Paid */}
                <motion.div
                  className="bg-white rounded-xl p-6 shadow flex items-center gap-4 border-2 border-amber-500"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="p-3 rounded-full bg-amber-100 text-amber-600">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">To Be Paid</span>
                    <span className="text-3xl font-bold text-gray-900">
                      {(totalCoins - totalPaidCoins).toLocaleString()} Coins
                    </span>
                    <span className="text-sm text-gray-500">
                      ₹ {(totalCoinValue - paidCoinValue).toFixed(2)}
                    </span>
                  </div>
                </motion.div>
              </div>

              {/* Coins List */}
              <div className="bg-purple-50 rounded-xl p-6 shadow-inner">
                <h3 className="text-xxl font-semibold text-logo mb-4 text-left">
                  Earning History
                </h3>

                {coinsGifted.length === 0 ? (
                  <p className="text-center text-gray-500">
                    No coins received yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {coinsGifted
                      .slice()
                      .sort(
                        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                      ) // latest first
                      .map((transaction, idx) => (
                        <motion.div
                          key={idx}
                          className="relative bg-white p-4 md:p-6 rounded-lg shadow hover:shadow-lg transition
     flex flex-row items-center gap-4"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 + idx * 0.1 }}
                        >
                          {/* Badge in top-right */}
                          <span
                            className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold ${
                              transaction.isPaid
                                ? "bg-green-100 text-green-600"
                                : "bg-red-100 text-red-600"
                            }`}
                          >
                            {transaction.isPaid ? "Paid" : "Not Paid"}
                          </span>

                          {/* Icon */}
                          <div className="bg-purple-100 p-2 rounded-full flex items-center justify-center">
                            <img
                              alt=""
                              src="./images/coin.svg"
                              className="h-7 w-7 text-logo"
                            />
                          </div>

                          {/* Text + Date */}
                          <div className="flex-1 flex flex-row items-center justify-between">
                            <span className="font-semibold text-xl text-logo flex items-center">
                              {transaction.coins} coins
                            </span>
                            <span className="text-sm text-gray-500 flex items-center">
                              {formatDateTime(transaction.createdAt)}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "ratings" && (
            <div className="space-y-6 px-4 py-6">
              {/* Overall Rating Card */}
              <motion.div
                className="bg-logo text-white rounded-xl p-6 shadow-md text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="text-lg font-semibold mb-1">
                  Total Reviews ({ratings.length})
                </h2>
                <div className="flex flex-col items-center justify-center">
                  <div className="flex items-center justify-center mb-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <StarIcon
                        key={i}
                        size={24}
                        className={`
                ${
                  i < avgRating
                    ? "text-yellow-400 stroke-yellow-400 fill-yellow-500"
                    : "text-gray-300 stroke-gray-300 fill-logo"
                }
                stroke-2
              `}
                      />
                    ))}
                  </div>
                  <span className="text-3xl font-bold">
                    {avgRating.toFixed(1)}
                  </span>
                </div>
              </motion.div>

              {/* Reviews List */}
              <div className="bg-purple-50 rounded-xl p-6 shadow-inner">
                <h3 className="text-xl font-semibold text-logo mb-4 text-center">
                  Reviews
                </h3>

                {ratings.length === 0 ? (
                  <p className="text-center text-gray-500">No reviews yet.</p>
                ) : (
                  <div className="space-y-3">
                    {ratings.map((c, idx) => (
                      <motion.div
                        key={c.id}
                        className="bg-white p-4 md:p-6 rounded-lg shadow hover:shadow-lg transition flex flex-col md:flex-row gap-4"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + idx * 0.1 }}
                      >
                        {/* Avatar and Name for mobile */}
                        <div className="flex items-center gap-3 md:hidden">
                          <img
                            src={c.user.avatar}
                            alt={c.user.display_name}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                          <h4 className="font-semibold text-logo">
                            {c.user.display_name}
                          </h4>
                        </div>

                        {/* Avatar for desktop */}
                        <img
                          src={c.user.avatar}
                          alt={c.user.display_name}
                          className="hidden md:block h-12 w-12 rounded-full object-cover"
                        />

                        {/* Name + Rating + Comment */}
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-0 mb-2">
                            {/* Name for desktop */}
                            <h4 className="hidden md:block font-semibold text-logo">
                              {c.user.display_name}
                            </h4>

                            {/* Rating Stars */}
                            <div className="flex items-center gap-2">
                              <div className="flex gap-1">
                                {[0, 1, 2, 3, 4].map((i) => (
                                  <StarIcon
                                    key={i}
                                    size={18}
                                    className={`
                      ${
                        i < c.rating
                          ? "text-yellow-400 stroke-yellow-400 fill-yellow-500"
                          : "text-gray-400 stroke-gray-300"
                      }
                      stroke-2
                    `}
                                  />
                                ))}
                              </div>
                              <span className="text-base md:text-lg text-logo">
                                {c.rating}
                              </span>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm md:text-base">
                            {c.comment}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SUPPORT */}
          {activeTab === "support" && (
            <div className="bg-white p-8 rounded-2xl shadow-lg pb-16">
              <h3 className="text-2xl font-bold text-logo mb-6">
                We're Here to Help
              </h3>

              <form onSubmit={handleSupportSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Select Reason
                  </label>
                  <select
                    value={supportReason}
                    onChange={(e) => setSupportReason(e.target.value)}
                    required
                    className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:border-logo focus:ring-logo"
                  >
                    <option value="">-- Choose reason --</option>
                    <option>Question about the app</option>
                    <option>Payment Issue</option>
                    <option>Question about the app</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    How can we help you?
                  </label>
                  <textarea
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    rows={6}
                    maxLength={500}
                    required
                    className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:border-logo focus:ring-logo"
                    placeholder="Describe your issue..."
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Max 500 characters
                  </p>
                </div>

                <div className="flex justify-end items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setSupportReason("");
                      setSupportMessage("");
                      setSupportResponse("");
                    }}
                    className="px-5 py-2 border border-logo text-logo rounded-lg hover:bg-purple-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-logo text-white rounded-lg hover:bg-logo disabled:opacity-50 transition"
                  >
                    {submitting ? "Submitting…" : "Submit"}
                  </button>
                </div>

                {supportResponse && (
                  <p className="mt-4 text-center text-gray-700">
                    {supportResponse}
                  </p>
                )}
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Edit Profile Popup */}
      {showEditProfile && (
        <EditProfilePopup
          onClose={() => setShowEditProfile(false)}
          profile={profileData}
          onSuccess={() => {
            setShowEditProfile(false);
            setProfileReloadKey((k) => k + 1);
          }}
        />
      )}
    </main>
  );
}
